import { ClientFeatureFlag } from '~/types/resources/ClientFeatureFlag';

/**
 * Presentation層で使用するフィーチャーフラグの型定義。
 */
export type ClientFeatureFlagFormType = {
  /** フィーチャーフラグのキー文字列 */
  featureKey: string;
  /** フラグが有効かどうか */
  isEnabled: boolean;
  /** 画面表示用ラベル */
  label: string;
};

/**
 * フィーチャーフラグキーに対応する表示用ラベルを返す。
 *
 * @param featureKey - APIから取得したフィーチャーフラグのキー文字列。
 * @returns 表示用ラベル。対応するラベルがない場合はキーをそのまま返す。
 */
export const getFeatureFlagLabel = (featureKey: string): string => {
  // 各フラグキーに対して個別の表示ラベルを定義する。
  // 対応するラベルがない場合はキー文字列をそのまま表示する。
  switch (featureKey) {
    case 'giftee_store':
      return 'コンビニ常時割引';
    case 'inesus_store':
      return '商業施設クーポン';
    default:
      return featureKey; // デフォルトはそのまま表示
  }
};

/**
 * APIレスポンス形式のフィーチャーフラグ配列をPresentation層の形式に変換する。
 *
 * @param clientFeatureFlags - APIから取得したフィーチャーフラグの配列。
 * @returns Presentation層向けにフォーマットされたフィーチャーフラグの配列。
 *          各要素は `featureKey`・`isEnabled`・`label` プロパティを持つ。
 */
export const convertClientFeatureFlags = (
  clientFeatureFlags: ClientFeatureFlag[]
): ClientFeatureFlagFormType[] => {
  return clientFeatureFlags.map(flag => ({
    featureKey: flag.feature_key,
    isEnabled: flag.enabled,
    label: getFeatureFlagLabel(flag.feature_key),
  }));
};

/**
 * Presentation層で使用するフィーチャーフラグの配列をAPIのリクエスト形式に変換する。
 *
 * @param clientFeatureFlags  - Presentation層で使用するフィーチャーフラグの配列。
 * @returns APIのリクエストボディに適した形式のオブジェクト。キーがフィーチャーフラグのキー、値が有効/無効を示す真偽値。
 *
 * 例:
 * 入力: [
 *   { featureKey: 'giftee_store', isEnabled: true, label: 'コンビニ常時割引' },
 *   { featureKey: 'another_feature', isEnabled: false, label: '別の機能' }
 * ]
 * 出力: {
 *   giftee_store: true,
 *   another_feature: false
 * }
 *
 * この関数は、Presentation層で管理されているフィーチャーフラグの配列をAPIのリクエスト形式に変換します。
 * 各フィーチャーフラグの `featureKey` をオブジェクトのキーとして使用し、`isEnabled` の値をそのままAPIに渡す形式に整形します。
 * これにより、APIとのデータのやり取りがスムーズになり、コードの可読性も向上します。
 */
export const formatClientFeatureFlagsForApi = (
  clientFeatureFlags: Omit<ClientFeatureFlagFormType, 'label'>[]
) => {
  return Object.fromEntries(clientFeatureFlags.map(flag => [flag.featureKey, flag.isEnabled]));
};
